import React, { useState, useEffect, useRef } from 'react';
import { Header, Loader } from 'semantic-ui-react';
import { getCumulativeMatchesOverTime } from '~/api/dashboard';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    Title,
    Tooltip,
    Legend
);

interface CumulativeData {
    quarter: string;
    projectArray: number;
    projectStorage: number;
}

const CumulativeMatchesOverTime: React.FC = () => {
    const [data, setData] = useState<CumulativeData[]>([]);
    const [loading, setLoading] = useState(true);
    const chartRef = useRef<HTMLCanvasElement>(null);
    const chartInstance = useRef<ChartJS | null>(null);
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                const result = await getCumulativeMatchesOverTime();
                setData(result || []);
            } catch (error) {
                console.error('Error fetching cumulative matches over time:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (!loading && data.length > 0 && chartRef.current) {
            // Destroy existing chart if it exists
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');
            if (ctx) {
                chartInstance.current = new ChartJS(ctx, {
                    type: 'bar',
                    data: {
                        labels: data.map(item => item.quarter),
                        datasets: [
                            {
                                label: 'Solar (MW)',
                                data: data.map(item => item.projectArray),
                                backgroundColor: '#BACB45',
                                borderColor: '#BACB45',
                                borderWidth: 1,
                            },
                            {
                                label: 'Storage (MW)',
                                data: data.map(item => item.projectStorage),
                                backgroundColor: '#D0DEE7',
                                borderColor: '#D0DEE7',
                                borderWidth: 1,
                            },
                        ],
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                position: 'top',
                            },
                            title: {
                                display: false,
                            },
                        },
                        scales: {
                            x: {
                                stacked: true,
                                title: {
                                    display: true,
                                    text: 'Quarter',
                                },
                            },
                            y: {
                                stacked: true,
                                title: {
                                    display: true,
                                    text: 'Megawatts (MW)',
                                },
                                beginAtZero: true,
                            },
                        },
                    },
                });
            }
        }

        // Cleanup function
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [data, loading]);

    if (loading) {
        return (
            <div className="card">
                <Header as="h2">Cumulative matched deals</Header>
                <Loader active inline="centered" />
            </div>
        );
    }

    return (
        <div className="card">
            <Header as="h2">Cumulative matched deals</Header>
            {data.length > 0 ? (
                <div style={{ height: '400px', width: '100%' }}>
                    <canvas ref={chartRef} />
                </div>
            ) : (
                <p>No data available</p>
            )}
        </div>
    );
};

export default CumulativeMatchesOverTime;